import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Upload, Trash2, Copy, Image, FileText, X } from 'lucide-react';
import { mediaService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function MediasPage() {
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadMedias();
  }, []);

  const loadMedias = async () => {
    try {
      const response = await mediaService.getAll();
      setMedias(response.data.media || []);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} n'est pas une image`);
        continue;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} est trop lourd (max 5MB)`);
        continue;
      }

      try {
        await mediaService.uploadImage(file);
        toast.success(`${file.name} uploadé`);
      } catch (error) {
        toast.error(`Erreur upload ${file.name}`);
      }
    }

    setUploading(false);
    loadMedias();
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce média ?')) return;
    
    try {
      await mediaService.delete(id);
      toast.success('Média supprimé');
      loadMedias();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = getFullUrl(url);
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copiée !');
  };

  // Fonction pour obtenir l'URL complète
  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const BASE_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${BASE_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Médiathèque</h1>
          <p className="text-dark-500">Gérez vos images et fichiers</p>
        </div>
        
        <label className="btn-primary cursor-pointer">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
            disabled={uploading}
          />
          {uploading ? (
            <>
              <LoadingSpinner size="sm" />
              <span className="ml-2">Upload...</span>
            </>
          ) : (
            <>
              <Upload className="w-5 h-5 mr-2" />
              Ajouter des images
            </>
          )}
        </label>
      </div>

      {medias.length === 0 ? (
        <div className="card p-12 text-center">
          <Image className="w-16 h-16 text-dark-300 mx-auto mb-4" />
          <p className="text-dark-500 mb-4">Aucun média</p>
          <label className="btn-primary cursor-pointer inline-flex">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleUpload}
              className="hidden"
            />
            <Upload className="w-5 h-5 mr-2" />
            Ajouter votre première image
          </label>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {medias.map((media, index) => (
            <motion.div
              key={media.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="group relative bg-white rounded-xl overflow-hidden shadow-sm border border-dark-100 hover:shadow-md transition-shadow"
            >
              {/* Image */}
              <div className="aspect-square bg-dark-100 relative">
                {media.mime_type?.startsWith('image/') ? (
                  <img
                    src={getFullUrl(media.url)}
                    alt={media.original_name || 'Image'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f0f0f0" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-size="12">Image</text></svg>';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="w-12 h-12 text-dark-400" />
                  </div>
                )}

                {/* Overlay avec actions */}
                <div className="absolute inset-0 bg-dark-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => copyUrl(media.url)}
                    className="p-2 bg-white rounded-lg hover:bg-dark-100 transition-colors"
                    title="Copier l'URL"
                  >
                    <Copy className="w-4 h-4 text-dark-700" />
                  </button>
                  <button
                    onClick={() => handleDelete(media.id)}
                    className="p-2 bg-white rounded-lg hover:bg-red-50 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>

              {/* Nom du fichier */}
              <div className="p-2">
                <p className="text-xs text-dark-600 truncate" title={media.original_name}>
                  {media.original_name || media.filename}
                </p>
                <p className="text-xs text-dark-400">
                  {media.file_size ? `${(media.file_size / 1024).toFixed(1)} KB` : ''}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MediasPage;